import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../utils/api';
import '../styles/JobDetail.css';

function JobDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadTemplate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const loadTemplate = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get(`/api/templates/${slug}`);
      setTemplate(data);
    } catch (err) {
      console.error('Failed to load template:', err);
      setError(err.status === 404 ? 'Service not found' : 'Failed to load service');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (cents) => {
    const dollars = cents / 100;
    return dollars < 10 ? `$${dollars.toFixed(2)}` : `$${Math.round(dollars)}`;
  };

  const formatTurnaround = (hours) => {
    if (!hours) return 'As soon as possible';
    if (hours < 24) return `${hours} hours`;
    const days = Math.round(hours / 24);
    return `${days} day${days > 1 ? 's' : ''}`;
  };

  // Generate a helpful description for each question type
  const getQuestionHint = (question) => {
    // Use placeholder if provided, otherwise generate based on question type
    if (question.placeholder) {
      return question.placeholder;
    }
    
    switch (question.type) {
      case 'select':
      case 'multiselect':
        return question.options?.length 
          ? `Options include: ${question.options.slice(0, 3).join(', ')}${question.options.length > 3 ? '...' : ''}`
          : null;
      case 'file':
        return question.accepted_types?.length 
          ? `We'll ask for files (${question.accepted_types.join(', ')})`
          : 'We may ask you to upload relevant files';
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="job-detail-page">
        <div className="job-detail-loading">
          <div className="loading-spinner"></div>
          <p>Loading service...</p>
        </div>
      </div>
    );
  }

  if (error && !template) {
    return (
      <div className="job-detail-page">
        <div className="job-detail-error">
          <h2>{error}</h2>
          <button onClick={() => navigate('/jobs')} className="btn btn-primary">
            Browse All Services
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="job-detail-page">
      <div className="job-detail-container">
        {/* Left: Template Info */}
        <div className="job-detail-info">
          <button onClick={() => navigate('/jobs')} className="back-link">
            ← Back to Services
          </button>

          <div className="job-detail-header">
            <span className="category-badge">{template.category}</span>
          </div>

          <h1 className="job-detail-title">{template.name}</h1>

          <div className="job-detail-price-section">
            <span className="job-detail-price">
              {formatPrice(template.price_cents)}
            </span>
            <span className="job-detail-turnaround">
              ⏱️ {formatTurnaround(template.turnaround_hours)}
            </span>
          </div>

          <p className="job-detail-description">{template.description}</p>

          {template.deliverable && (
            <div className="job-detail-section">
              <h3>What You'll Get</h3>
              <p className="deliverable-text">{template.deliverable}</p>
            </div>
          )}

          {template.quality_criteria && template.quality_criteria.length > 0 && (
            <div className="job-detail-section">
              <h3>Features</h3>
              <ul className="quality-list">
                {template.quality_criteria.map((criterion, i) => (
                  <li key={i}>✓ {criterion}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Right: Things to Think About */}
        <div className="job-detail-form-section">
          <div className="job-detail-form-card">
            <h2>Things to Think About</h2>
            <p className="form-subtitle">
              We'll walk you through these questions in chat to build exactly what you need.
            </p>

            {template.intake_questions && template.intake_questions.length > 0 ? (
              <ul className="things-to-think-about">
                {template.intake_questions.slice(0, 10).map((question, i) => (
                  <li key={i} className="think-item">
                    <span className="think-question">{question.question}</span>
                    {getQuestionHint(question) && (
                      <span className="think-hint">{getQuestionHint(question)}</span>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="no-questions">
                No specific requirements—just tell us your vision and we'll take it from there.
              </p>
            )}

            <Link 
              to={`/chat?service=${template.slug}`}
              className="btn btn-primary btn-large btn-full get-started-btn"
            >
              Get Started Now →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default JobDetail;
